import { buildFrameConfigFromChosenOptions } from './frameConfigFromOptions.js'
import { artworkFitsMatWidthConstraints, constraintFitsDimensions, resolveResizeConstraintsFromChosenOptions } from './sizingPolicy.js'

export const isMatColorCategory = name => /^mat\s*[123]$/i.test(String(name || '').trim())
export const isMatWidthCategory = name => /mat\s*width/i.test(String(name || ''))
const matKey = option => JSON.stringify([option.name, String(option.value ?? ''), option.extra_data?.material || ''])
const sheetArea = option => {
  const { max_width: width, max_height: height } = option.extra_data || {}
  return Number(width) > 0 && Number(height) > 0 ? Number(width) * Number(height) : Infinity
}
const chosenSheet = option => option.option_id != null ? option : ({
  name: option.name, value: option.value ?? null, option_id: option.id, extra_data: option.extra_data || null,
})

/** A choice is a mat name/material within a slot and substrate; sheets retain their saved IDs. */
export function groupMatChoices(options = [], substrate) {
  const choices = new Map()
  for (const option of options) {
    if (substrate && option.substrate && option.substrate !== substrate) continue
    const key = matKey(option)
    if (!choices.has(key)) choices.set(key, [])
    choices.get(key).push(option)
  }
  return choices
}

/** One rule for menu availability, actual sheet selection, and maximum-sheet limits. */
export function resolveMatChoice(sheets = [], { selected, width, height, matWidthsArray = [] } = {}) {
  const candidates = selected ? [selected, ...sheets] : sheets
  let sheet = null, largest = null, hasUnavailable = false
  for (const candidate of candidates) {
    const area = sheetArea(candidate)
    if (!largest || (Number.isFinite(area) && (area > sheetArea(largest) || !Number.isFinite(sheetArea(largest))))) largest = candidate
    if (!artworkFitsMatWidthConstraints(width, height, [chosenSheet(candidate)], matWidthsArray)) {
      hasUnavailable = true
    } else if (!sheet || area < sheetArea(sheet)) sheet = candidate
  }
  return { sheet, largest, hasUnavailable }
}

export function largestMatchingMatSheets(chosenOptions = [], availableOptions = [], substrate) {
  const choices = groupMatChoices(availableOptions, substrate)
  return chosenOptions.map(selected => isMatColorCategory(selected.name)
    ? chosenSheet(resolveMatChoice(choices.get(matKey(selected)), { selected }).largest)
    : selected)
}

/** Intersect color/material choices across item configs; sheet IDs can differ per item. */
export function resolveMatMenu({ category, items = [], availableOptions = [] } = {}) {
  let common = null, hasUnavailable = false
  for (const { width, height, substrate, chosen_options: chosenOptions = [] } of items) {
    const catalog = availableOptions.filter(option => !substrate || !option.substrate || option.substrate === substrate)
    const choices = groupMatChoices(catalog)
    // Measure the prospective stack, retaining saved widths and filling missing defaults.
    const stack = [...chosenOptions]
    for (const option of catalog.filter(option => isMatWidthCategory(option.name))) {
      if (!stack.some(chosen => chosen.name === option.name)) {
        stack.push(chosenSheet(catalog.find(candidate => candidate.name === option.name && candidate.is_default) || option))
      }
    }
    if (!stack.some(option => option.name === category)) {
      stack.push({ name: category, extra_data: { color: '#FFFFFF' } })
    }
    const { matWidthsArray } = buildFrameConfigFromChosenOptions(substrate, stack)
    const size = { width, height, matWidthsArray }
    const fixedFit = chosenOptions.filter(option => isMatColorCategory(option.name) && option.name !== category)
      .every(selected => resolveMatChoice(choices.get(matKey(selected)), { ...size, selected }).sheet)
    const fitting = new Map()
    for (const [key, sheets] of choices) {
      if (sheets[0].name !== category) continue
      const result = resolveMatChoice(sheets, size)
      if (fixedFit && result.sheet) fitting.set(key, result.sheet)
      hasUnavailable ||= !fixedFit || result.hasUnavailable
    }
    const intersection = common ? new Map([...common].filter(([key]) => fitting.has(key))) : fitting
    hasUnavailable ||= intersection.size < fitting.size || (common != null && intersection.size < common.size)
    common = intersection
  }
  return { options: [...(common?.values() || [])], hasUnavailable }
}

/** Resolve the stack together: removing a layer also changes the remaining sheets' required size. */
export function resolveMatSelection({ width, height, substrate, chosenOptions = [], availableOptions = [] } = {}) {
  availableOptions = availableOptions.filter(option => !substrate || !option.substrate || option.substrate === substrate)
  const choices = groupMatChoices(availableOptions, substrate)
  const mats = chosenOptions.filter(option => isMatColorCategory(option.name))
  const nonMats = chosenOptions.filter(option => !isMatColorCategory(option.name))
  let remaining = mats
  while (remaining.length) {
    const candidate = [...nonMats, ...remaining]
    const { matWidthsArray = [] } = buildFrameConfigFromChosenOptions(substrate, candidate) || {}
    const constraints = resolveResizeConstraintsFromChosenOptions(candidate, { matWidthsArray })
    if (constraints.some(constraint => constraint.source === 'frame' && !constraintFitsDimensions(width, height, constraint))) {
      remaining = []
      break
    }
    const next = remaining.map(selected => resolveMatChoice(choices.get(matKey(selected)), {
      selected, width, height, matWidthsArray,
    }).sheet).filter(Boolean).map(chosenSheet)
    const removed = next.length < remaining.length
    remaining = next
    if (!removed) break
  }
  const resolved = chosenOptions.map(option => isMatColorCategory(option.name)
    ? remaining.find(mat => mat.name === option.name) : option).filter(Boolean)
  return {
    chosenOptions: normalizeChosenMatSupportOptions(resolved, availableOptions, remaining.length),
    removed: mats.filter(mat => !remaining.some(option => option.name === mat.name)),
  }
}

export function normalizeChosenMatSupportOptions(chosenOptions = [], availableOptions = [], matCount = 0) {
  const normalizedCount = Math.max(0, Number(matCount) || 0)
  const countOption = availableOptions.find((option) => {
    if (!/number\s*of\s*mats/i.test(String(option?.name || ''))) return false
    return parseInt(option.value ?? option.num_value ?? option.char_value, 10) === normalizedCount
  })
  const normalizedCountOption = countOption
    ? {
        name: countOption.name,
        value: countOption.value,
        option_id: countOption.id,
        extra_data: countOption.extra_data || {},
      }
    : null
  const next = []
  let countOptionAdded = false

  chosenOptions.forEach((option) => {
    const name = String(option?.name || '')
    if (/number\s*of\s*mats/i.test(name)) {
      if (normalizedCountOption) next.push(normalizedCountOption)
      countOptionAdded = true
      return
    }
    if (normalizedCount === 0 && isMatWidthCategory(name)) return
    next.push(option)
  })

  if (!countOptionAdded && normalizedCountOption) next.push(normalizedCountOption)
  return next
}
